import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ClipboardCheck, 
  Search, 
  Plus,
  Edit,
  Trash2,
  X,
  Calendar,
  Star,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  Filter,
  FileText,
  FileSpreadsheet,
  RefreshCcw,
  LayoutGrid,
  Award,
  Target,
  Download,
  Upload,
  ChevronDown,
  ChevronUp,
  FileOutput,
  User,
  Briefcase,
  CheckCircle as CheckCircleIcon,
  List,
  LayoutList,
  Check,
  Gauge,
  MessageSquare} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  buildHrEmployeeSelectOptions,
  buildHrEmployeePickerPatch,
  resolveHrEmployeeFromLabel,
  useHrEmployees,
  sliceHrListItems,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import localApiAdapter from '../../core/services/localApiAdapter';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { useHR } from '../../core/contexts/HRContext';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'employee', labelKey: 'col_employee' },
  { key: 'evaluator', labelKey: 'col_evaluator' },
  { key: 'period', labelKey: 'col_period' },
  { key: 'score', labelKey: 'col_global_score' },
  { key: 'date', labelKey: 'col_date' },
  { key: 'performance', labelKey: 'col_performance' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const EvaluationsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.evaluationsPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const hrConfig = useHrConfig();
  const { employees: realEmployees } = useHrEmployees();

  // Use HR Context for real data
  const { 
    evaluations, 
    addEvaluation, 
    updateEvaluation, 
    removeEvaluation, 
    refreshData,
    loading 
  } = useHR();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [viewingEvaluation, setViewingEvaluation] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  const [selectedEvaluations, setSelectedEvaluations] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [mainViewMode, setMainViewMode] = useState('all_evaluations');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  
  // Date range filter (Date objects, aligned with Employees / Training)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const [formTab, setFormTab] = useState('evaluation');
  

  const [formData, setFormData] = useState({
    employeeId: '',
    employeeName: '',
    evaluator: '',
    period: '',
    date: new Date().toISOString().split('T')[0],
    performance: 3,
    competences: 3,
    comportement: 3,
    objectifs: 3,
    noteGlobale: 3,
    strengths: '',
    weaknesses: '',
    objectives: '',
    comments: '',
    bonusAmount: ''
  });

  const triggerRefresh = () => refreshData();

  const noteNum = (e) => {
    const n = Number(e?.noteGlobale);
    return Number.isFinite(n) ? n : 0;
  };
  const stats = {
    total: evaluations.length,
    excellent: evaluations.filter(e => noteNum(e) >= 4.5).length,
    bon: evaluations.filter(e => noteNum(e) >= 3.5 && noteNum(e) < 4.5).length,
    moyen: evaluations.filter(e => noteNum(e) >= 2.5 && noteNum(e) < 3.5).length,
    faible: evaluations.filter(e => noteNum(e) < 2.5).length,
    avgScore:
      evaluations.length > 0
        ? (
            evaluations.reduce((sum, e) => sum + noteNum(e), 0) / evaluations.length
          ).toFixed(2)
        : '0.00'
  };

  const evaluationDistribution = {
    all_evaluations: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'excellent', name: p('dist_excellent'), count: stats.excellent, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'bon', name: p('dist_bon'), count: stats.bon, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'moyen', name: p('dist_moyen'), count: stats.moyen, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'faible', name: p('dist_faible'), count: stats.faible, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_performance: {
      title: p('dist_by_performance_title'),
      data: [
        { filterKey: 'excellent', name: p('rating_excellent'), count: stats.excellent, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'bon', name: p('rating_bon'), count: stats.bon, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'moyen', name: p('rating_moyen'), count: stats.moyen, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'faible', name: p('rating_faible'), count: stats.faible, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_periode: {
      title: p('dist_by_period_title'),
      data: [...new Set(evaluations.map(e => e.period))].map(period => ({
        filterKey: period,
        name: period,
        count: evaluations.filter(e => e.period === period).length,
        color: "bg-gradient-to-r from-purple-500 to-purple-600"
      }))
    }
  };

  const distributionInfo = evaluationDistribution[mainViewMode] || evaluationDistribution.all_evaluations;

  const mainTabs = [
    { id: 'all_evaluations', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_performance', name: p('tab_by_performance'), icon: TrendingUp },
    { id: 'par_periode', name: p('tab_by_period'), icon: Calendar }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all_f'), icon: ClipboardCheck },
    { id: 'excellent', name: p('filter_excellent'), icon: Award },
    { id: 'bon', name: p('filter_bon'), icon: TrendingUp },
    { id: 'moyen', name: p('filter_moyen'), icon: Minus },
    { id: 'faible', name: p('filter_faible'), icon: TrendingDown }
  ];

  const getEvaluationTier = (note) => {
    const n = parseFloat(note);
    if (Number.isNaN(n)) return 'moyen';
    if (n >= 4.5) return 'excellent';
    if (n >= 3.5) return 'bon';
    if (n >= 2.5) return 'moyen';
    return 'faible';
  };

  const getPerformanceTierLabel = (tier) => {
    const map = {
      excellent: p('rating_excellent'),
      bon: p('rating_bon'),
      moyen: p('rating_moyen'),
      faible: p('rating_faible'),
    };
    return map[tier] || tier;
  };

  const periodHintPresets = ['2026 — مراجعة سنوية', '2025 — مراجعة سنوية', '2026 — النصف الأول', '2026 — النصف الثاني'];
  const periodSelectOptions = [
    { value: '', label: p('select_period') },
    ...[
      ...new Set(
        [
          ...periodHintPresets,
          ...evaluations.map((e) => e.period).filter((p) => p && String(p).trim()),
          ...(formData.period ? [formData.period] : [])
        ].filter(Boolean)
      )
    ].map((p) => ({ value: p, label: p }))
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEvaluations = useMemo(
    () => sliceHrListItems(
      evaluations.filter((evaluation) => {
    const matchesSearch = !searchTerm ||
      evaluation.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evaluation.evaluator?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evaluation.period?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    if (statusFilter !== 'all' && getEvaluationTier(evaluation.noteGlobale) !== statusFilter) {
      return false;
    }

    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_evaluations':
        case 'par_performance':
          if (getEvaluationTier(evaluation.noteGlobale) !== activeDistributionFilter) return false;
          break;
        case 'par_periode':
          if (evaluation.period !== activeDistributionFilter) return false;
          break;
        default:
          break;
      }
    }

    return true;
  }),
      hrConfig.itemsPerPage,
    ),
    [evaluations, searchTerm, statusFilter, activeDistributionFilter, mainViewMode, hrConfig.itemsPerPage],
  );

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredEvaluations, selectedEvaluations, setSelectedEvaluations);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.employeeName || !formData.employeeName.trim()) {
      alert(p('validation_employee_required'));
      return;
    }
    
    if (!formData.date) {
      alert(p('validation_date_required'));
      return;
    }
    
    const avg = (parseFloat(formData.performance) + parseFloat(formData.competences) + parseFloat(formData.comportement) + parseFloat(formData.objectifs)) / 4;
    const updatedData = { 
      ...formData, 
      noteGlobale: avg,
      date: formData.date || new Date().toISOString().split('T')[0]
    };
    
      console.log('[EvaluationsPage] submitting:', updatedData);
    
    try {
      if (editingEvaluation) {
        await updateEvaluation(editingEvaluation.id, updatedData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التقييم' }));
      } else {
        await addEvaluation(updatedData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التقييم' }));
      }
      resetForm();
    } catch (error) {
      console.error('Error saving evaluation:', error);
      notifyError(showNotify, p('save_error_evaluation'));
    }
  };

  const handleEdit = (evaluation) => {
    setEditingEvaluation(evaluation);
    setFormData(evaluation);
    setFormTab('evaluation');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await removeEvaluation(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التقييم' }));
      } catch (error) {
        console.error('Error deleting evaluation:', error);
        notifyError(showNotify, p('alert_delete_error'));
      }
    }
  };

  const handleView = (evaluation) => {
    setViewingEvaluation(evaluation);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      employeeName: '',
      evaluator: '',
      period: '',
      date: new Date().toISOString().split('T')[0],
      performance: 3,
      competences: 3,
      comportement: 3,
      objectifs: 3,
      noteGlobale: 3,
      strengths: '',
      weaknesses: '',
      objectives: '',
      comments: '',
      bonusAmount: ''
    });
    setEditingEvaluation(null);
    setFormTab('evaluation');
    setShowModal(false);
  };

  const openNewEvaluationModal = () => {
    setEditingEvaluation(null);
    setFormData({
      employeeId: '',
      employeeName: '',
      evaluator: '',
      period: '',
      date: new Date().toISOString().split('T')[0],
      performance: 3,
      competences: 3,
      comportement: 3,
      objectifs: 3,
      noteGlobale: 3,
      strengths: '',
      weaknesses: '',
      objectives: '',
      comments: '',
      bonusAmount: ''
    });
    setFormTab('evaluation');
    setShowModal(true);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedEvaluations.length }))) {
      try {
        const deleteCount = selectedEvaluations.length;
        await Promise.all(selectedEvaluations.map(id => removeEvaluation(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'تقييم' }));
        setSelectedEvaluations([]);
      } catch (error) {
        console.error('Error bulk deleting:', error);
        notifyError(showNotify, p('alert_delete_error'));
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredEvaluations.map(e => ({
        [p('col_employee')]: e.employeeName || p('na'),
        [p('col_evaluator')]: e.evaluator || p('na'),
        [p('col_period')]: e.period || p('na'),
        [p('col_global_score')]: e.noteGlobale != null ? e.noteGlobale.toFixed(2) : '0.00',
        [p('col_performance')]: e.performance || p('na'),
        [p('col_competences')]: e.competences || p('na'),
        [p('col_bonus')]: e.bonusAmount != null && e.bonusAmount !== '' ? formatBrandMoney(e.bonusAmount) : p('na'),
        [p('col_date')]: e.date ? new Date(e.date).toLocaleDateString(dateLocale) : p('na')
      }));
      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredEvaluations.map(e => ({
        [p('col_employee')]: e.employeeName || p('na'),
        [p('col_evaluator')]: e.evaluator || p('na'),
        [p('col_period')]: e.period || p('na'),
        [p('col_global_score')]: e.noteGlobale != null ? e.noteGlobale : 0,
        [p('col_performance')]: e.performance || p('na'),
        [p('col_competences')]: e.competences || p('na'),
        [p('col_behaviour')]: e.comportement || p('na'),
        [p('col_objectives')]: e.objectifs || p('na'),
        [p('col_bonus')]: e.bonusAmount != null && e.bonusAmount !== '' ? formatBrandMoney(e.bonusAmount) : p('na'),
        [p('col_date')]: e.date ? new Date(e.date).toLocaleDateString(dateLocale) : p('na')
      }));
      await generateReportExcel(p('report_title'), reportData);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('filter_all_f') },
    { value: 'excellent', label: p('filter_excellent') },
    { value: 'bon', label: p('filter_bon') },
    { value: 'moyen', label: p('filter_moyen') },
    { value: 'faible', label: p('filter_faible') },
  ], [p]);

  const listStats = useMemo(() => [
    { key: 'total', label: p('stat_total'), value: stats.total, icon: ClipboardCheck, variant: 'copper' },
    { key: 'excellent', label: p('stat_excellent'), value: stats.excellent, icon: Award, variant: 'forest' },
    { key: 'bon', label: p('stat_bon'), value: stats.bon, icon: TrendingUp, variant: 'copper' },
    { key: 'moyen', label: p('stat_moyen'), value: stats.moyen, icon: Minus, variant: 'forest' },
  ], [stats, p]);

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const importExportProps = useMemo(() => buildHrImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
    isGeneratingPDF,
    handlers: { onExportExcel: handleExportExcel, onExportPDF: handleExportPDF },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t]);
  const evaluationFormTabs = useMemo(() => ([
    { id: 'evaluation', label: p('section_basic'), icon: Star, iconClassName: 'text-blue-400' },
    { id: 'feedbackInfo', label: p('section_feedback'), icon: MessageSquare, iconClassName: 'text-purple-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-0">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setStatusFilter('all'); setActiveDistributionFilter(null); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}>
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_evaluations' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} onClick={() => { setStatusFilter(filter.id); setActiveDistributionFilter(null); }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  statusFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}>
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
          <button
            key={item.filterKey || item.name}
            type="button"
            onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
            className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left w-full ${
              activeDistributionFilter === item.filterKey
                ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-orange-500/20'
                : 'border-white/10 hover:border-white/20 hover:bg-white/10'
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
              <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${(() => {
                const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
              })()}%` }} />
            </div>
            <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>
              {p('percent_of_items', { percent: (() => {
                const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
              })() })}
            </p>
          </button>
        ))}
        </div>
      </motion.div>
    </div>
  );

  const evaluationTierOptions = [
    { value: 'excellent', label: p('rating_excellent'), icon: Award, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'bon', label: p('rating_bon'), icon: TrendingUp, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'moyen', label: p('rating_moyen'), icon: Minus, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'faible', label: p('rating_faible'), icon: TrendingDown, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/evaluations/${recordId}`, { status: newStatus });      await refreshData();
      const tierNote = { excellent: 4.75, bon: 4, moyen: 3, faible: 2 };
      const v = tierNote[newStatus];
      if (v != null) {
        setViewingEvaluation((prev) =>
          prev && prev.id === recordId
            ? { ...prev, noteGlobale: v, performance: v, competences: v, comportement: v, objectifs: v }
            : prev
        );
      }
    } catch (error) {
      console.error('Error updating evaluation tier:', error);
    }
  };

    return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={ClipboardCheck}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedEvaluations.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedEvaluations.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewEvaluationModal}
        createLabel={p('new_item')}
        importExport={importExportProps}
        headerActions={<button type="button" onClick={triggerRefresh} className="refresh-button" title={p('refresh')}><RefreshCcw size={20} /></button>}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} /></div></th>
                  {isColVisible('employee') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_employee')}</th>}
                  {isColVisible('evaluator') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_evaluator')}</th>}
                  {isColVisible('period') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_period')}</th>}
                  {isColVisible('score') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_global_score')}</th>}
                  {isColVisible('date') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_date')}</th>}
                  {isColVisible('performance') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_performance')}</th>}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredEvaluations.length > 0 ? (
                  filteredEvaluations.map((evaluation) => (
                    <tr key={evaluation.id} className={`hover:bg-white/5 ${selectedEvaluations.includes(evaluation.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedEvaluations.includes(evaluation.id)} onChange={() => handleSelectRecord(evaluation.id)} /></div></td>
                      {isColVisible('employee') && <td className="px-6 py-4 text-white font-semibold">{evaluation.employeeName}</td>}
                      {isColVisible('evaluator') && <td className="px-6 py-4 text-white/80">{evaluation.evaluator}</td>}
                      {isColVisible('period') && <td className="px-6 py-4 text-white/80">{evaluation.period || '—'}</td>}
                      {isColVisible('score') && <td className="px-6 py-4 text-white/80">{evaluation.noteGlobale != null ? Number(evaluation.noteGlobale).toFixed(2) : '—'}</td>}
                      {isColVisible('date') && (
                        <td className="px-6 py-4 text-white/80">
                          {evaluation.date && !Number.isNaN(new Date(evaluation.date).getTime())
                            ? new Date(evaluation.date).toLocaleDateString(dateLocale)
                            : '—'}
                        </td>
                      )}
                      {isColVisible('performance') && (
                        <td className="px-6 py-4 text-white/80">{getPerformanceTierLabel(getEvaluationTier(evaluation.noteGlobale))}</td>
                      )}
                      <td className="px-6 py-4 text-center align-middle"><QawaemErpRowActions onView={() => handleView(evaluation)} onEdit={() => handleEdit(evaluation)} onDelete={() => handleDelete(evaluation.id)} viewTitle={t('common.view')} editTitle={t('common.edit')} deleteTitle={t('common.delete')} /></td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ClipboardCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredEvaluations.length > 0 ? (
              filteredEvaluations.map((evaluation, index) => (
                <motion.div
                  key={evaluation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ClipboardCheck size={20} className="text-blue-400 shrink-0" />
                          <span className="truncate">{evaluation.employeeName}</span>
                        </h3>
                        <QawaemClickableStatusBadge status={getEvaluationTier(evaluation.noteGlobale)} recordId={evaluation.id} options={evaluationTierOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        {evaluation.period ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {evaluation.period}
                          </span>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_basic')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-blue-400 shrink-0" />
                              <span className="font-medium">{p('col_evaluator')} :</span>{' '}
                              <span className="text-white">{evaluation.evaluator || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-violet-400 shrink-0" />
                              <span>
                                {evaluation.date && !isNaN(new Date(evaluation.date))
                                  ? new Date(evaluation.date).toLocaleDateString(dateLocale)
                                  : '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Star size={16} className="text-yellow-400 shrink-0 fill-yellow-400" />
                              <span className="text-white font-semibold">{evaluation.noteGlobale?.toFixed(2) ?? '—'}/5</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('scores_title')}</h5>
                          <div className="grid grid-cols-2 gap-2 text-sm text-white/70">
                            <span>{p('criteria_performance')} : <span className="text-white">{evaluation.performance}</span></span>
                            <span>{p('col_competences')} : <span className="text-white">{evaluation.competences}</span></span>
                            <span>{p('col_behaviour')} : <span className="text-white">{evaluation.comportement}</span></span>
                            <span>{p('col_objectives')} : <span className="text-white">{evaluation.objectifs}</span></span>
                            {evaluation.bonusAmount != null && evaluation.bonusAmount !== '' && (
                              <span className="col-span-2">{p('col_bonus')} : <span className="text-white">{formatBrandMoney(evaluation.bonusAmount)}</span></span>
                            )}
                          </div>
                        </div>
                        <div className="md:col-span-2 lg:col-span-1">
                          <h5 className="font-medium text-white mb-2">{p('comments_title')}</h5>
                          <p className="text-sm text-white/60 line-clamp-3">
                            {evaluation.comments || evaluation.strengths || '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      onView={() => handleView(evaluation)}
                      onEdit={() => handleEdit(evaluation)}
                      onDelete={() => handleDelete(evaluation.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <ClipboardCheck size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingEvaluation ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="evaluation-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircleIcon size={18} />
                  {editingEvaluation ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            
            
            <HrFormModalTabBar
              tabs={evaluationFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingEvaluation ? p('modal_edit') : p('modal_new')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="evaluation-form" onSubmit={handleSubmit} >
                {formTab === 'evaluation' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_employee')}</label>
                            <CustomSelect
                              value={formData.employeeName}
                              onChange={(value) => {
                                const selectedEmployee = resolveHrEmployeeFromLabel(realEmployees, value);
                                if (selectedEmployee) {
                                  setFormData({ ...formData, ...buildHrEmployeePickerPatch(selectedEmployee, 'employeeName', 'employeeId') });
                                } else {
                                  setFormData({ ...formData, employeeName: value, employeeId: '' });
                                }
                              }}
                              options={buildHrEmployeeSelectOptions(realEmployees)}
                              placeholder={p('select_placeholder')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{t('modules.ressources_humaines.pages.accidentsPage.hint_employee_select')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_evaluator')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.evaluator}
                              onChange={(value) => setFormData({ ...formData, evaluator: value })}
                              options={buildHrEmployeeSelectOptions(realEmployees)}
                              placeholder={p('placeholder_evaluator')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{t('modules.ressources_humaines.pages.accidentsPage.hint_employee_select')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_period')}</label>
                            <CustomSelect
                              value={formData.period}
                              onChange={(value) => setFormData({ ...formData, period: value })}
                              options={periodSelectOptions}
                              placeholder={p('select_period')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('period_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_date')}</label>
                            <CustomDatePicker
                              value={formData.date}
                              onChange={(value) => setFormData({ ...formData, date: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'evaluation' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('criteria_performance')}</label>
                            <CustomNumberInput
                              value={formData.performance}
                              onChange={(value) => setFormData({ ...formData, performance: parseFloat(value) || 3 })}
                              placeholder={p('placeholder_score')}
                              min={1}
                              max={5}
                              step={0.5}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_competences')} (1-5)</label>
                            <CustomNumberInput
                              value={formData.competences}
                              onChange={(value) => setFormData({ ...formData, competences: parseFloat(value) || 3 })}
                              placeholder={p('placeholder_score')}
                              min={1}
                              max={5}
                              step={0.5}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_behaviour')} (1-5)</label>
                            <CustomNumberInput
                              value={formData.comportement}
                              onChange={(value) => setFormData({ ...formData, comportement: parseFloat(value) || 3 })}
                              placeholder={p('placeholder_score')}
                              min={1}
                              max={5}
                              step={0.5}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_objectives')} (1-5)</label>
                            <CustomNumberInput
                              value={formData.objectifs}
                              onChange={(value) => setFormData({ ...formData, objectifs: parseFloat(value) || 3 })}
                              placeholder={p('placeholder_score')}
                              min={1}
                              max={5}
                              step={0.5}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_bonus')}</label>
                            <CustomNumberInput
                              value={formData.bonusAmount}
                              onChange={(value) => setFormData({ ...formData, bonusAmount: value })}
                              placeholder={p('placeholder_bonus')}
                              min={0}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'feedbackInfo' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('view_section_strengths')}</label>
                            <textarea 
                              value={formData.strengths}
                              onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                              rows={2} 
                              placeholder={p('placeholder_strengths')} 
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('view_section_improvements')}</label>
                            <textarea 
                              value={formData.weaknesses}
                              onChange={(e) => setFormData({ ...formData, weaknesses: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                              rows={2} 
                              placeholder={p('placeholder_weaknesses')} 
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('comments_title')}</label>
                            <textarea 
                              value={formData.comments}
                              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                              rows={2} 
                              placeholder={p('placeholder_comments')} 
                            />
                          </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && viewingEvaluation && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingEvaluation(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title', { name: viewingEvaluation.employeeName })}</h2>
                <button
                  onClick={() => { setShowViewModal(false); setViewingEvaluation(null); }}
                  className="text-white/70 hover:text-white transition-colors"
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>

              <div >
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <ClipboardCheck size={18} className="text-blue-400" />
                    {p('view_section_general')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_employee')}</p>
                      <p className="text-white font-medium text-lg">{viewingEvaluation.employeeName || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_evaluator')}</p>
                      <p className="text-white font-medium">{viewingEvaluation.evaluator || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('field_period')}</p>
                      <p className="text-white font-medium">{viewingEvaluation.period || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('field_date')}</p>
                      <p className="text-white font-medium">
                        {viewingEvaluation.date && !isNaN(new Date(viewingEvaluation.date))
                          ? new Date(viewingEvaluation.date).toLocaleDateString(dateLocale)
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Star size={18} className="text-amber-400" />
                    {p('view_section_rating')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_status')}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <QawaemClickableStatusBadge status={getEvaluationTier(viewingEvaluation.noteGlobale)} recordId={viewingEvaluation.id} options={evaluationTierOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_global_score')}</p>
                      <p className="text-white font-medium text-xl text-amber-300">
                        {viewingEvaluation.noteGlobale != null ? `${viewingEvaluation.noteGlobale.toFixed(2)}/5` : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                        <TrendingUp size={14} className="text-blue-400 shrink-0" />
                        {p('col_performance')}
                      </p>
                      <p className="text-white font-medium">
                        {viewingEvaluation.performance != null ? `${viewingEvaluation.performance}/5` : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                        <Award size={14} className="text-green-400 shrink-0" />
                        {p('col_competences')}
                      </p>
                      <p className="text-white font-medium">
                        {viewingEvaluation.competences != null ? `${viewingEvaluation.competences}/5` : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                        <User size={14} className="text-purple-400 shrink-0" />
                        {p('col_behaviour')}
                      </p>
                      <p className="text-white font-medium">
                        {viewingEvaluation.comportement != null ? `${viewingEvaluation.comportement}/5` : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                        <Target size={14} className="text-orange-400 shrink-0" />
                        {p('col_objectives')}
                      </p>
                      <p className="text-white font-medium">
                        {viewingEvaluation.objectifs != null ? `${viewingEvaluation.objectifs}/5` : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_bonus')}</p>
                      <p className="text-white font-medium text-amber-300">
                        {viewingEvaluation.bonusAmount != null && viewingEvaluation.bonusAmount !== ''
                          ? formatBrandMoney(viewingEvaluation.bonusAmount)
                          : p('na')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp size={18} className="text-green-400" />
                      {p('view_section_strengths')}
                    </h3>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_comment')}</p>
                      <p className="text-white font-medium whitespace-pre-wrap">
                        {viewingEvaluation.strengths?.trim() ? viewingEvaluation.strengths : p('na')}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <TrendingDown size={18} className="text-orange-400" />
                      {p('view_section_improvements')}
                    </h3>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_comment')}</p>
                      <p className="text-white font-medium whitespace-pre-wrap">
                        {viewingEvaluation.weaknesses?.trim() ? viewingEvaluation.weaknesses : p('na')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default EvaluationsPage;


